import { Helmet } from 'react-helmet-async';

interface MetaTagsProps {
  title: string;
  description: string;
  timerMode: 'custom' | 'pomodoro';
  pomodoroPhase?: 'work' | 'shortBreak' | 'longBreak';
  timeRemaining?: string;
  isRunning: boolean;
}

export function MetaTags({ 
  title, 
  description, 
  timerMode, 
  pomodoroPhase, 
  timeRemaining,
  isRunning 
}: MetaTagsProps) {
  const getPhaseText = () => {
    if (timerMode !== 'pomodoro' || !pomodoroPhase) return '';
    switch (pomodoroPhase) {
      case 'work': return 'Work Session';
      case 'shortBreak': return 'Short Break';
      case 'longBreak': return 'Long Break';
    }
  };

  const dynamicTitle = timerMode === 'pomodoro'
    ? `${getPhaseText()}${timeRemaining ? ` - ${timeRemaining}` : ''} | ${title}`
    : `${timeRemaining ? `${timeRemaining} | ` : ''}${title}`;

  const dynamicDescription = timerMode === 'pomodoro'
    ? `${description} Currently in ${getPhaseText().toLowerCase()}${isRunning ? ' - Timer running' : ''}.`
    : `${description}${timeRemaining ? ` Current timer: ${timeRemaining}` : ''}${isRunning ? ' - Timer running' : ''}.`;

  const canonicalUrl = 'https://timertiktok.com';
  const ogImage = 'https://images.unsplash.com/photo-1584636633449-6edaf1c30b9e?auto=format&fit=crop&w=1200&h=630';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": title,
    "description": dynamicDescription,
    "applicationCategory": "Productivity",
    "operatingSystem": "Any",
    "url": canonicalUrl,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "author": {
      "@type": "Organization",
      "name": "TimerTikTok",
      "url": canonicalUrl
    },
    "dateModified": new Date().toISOString(),
    "image": ogImage
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="en" />
      <title>{dynamicTitle}</title>
      <meta name="description" content={dynamicDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:site_name" content="TimerTikTok" />
      <meta property="og:title" content={dynamicTitle} />
      <meta property="og:description" content={dynamicDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={dynamicTitle} />
      <meta name="twitter:description" content={dynamicDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* PWA */}
      <meta name="application-name" content={title} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={title} />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="theme-color" content="#ffffff" />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="keywords" content="timer, countdown, pomodoro, productivity, time management, focus timer, work timer, study timer" />
      
      {/* Preconnect to External Resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://images.unsplash.com" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}